import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { Feature, FeatureCollection, Polygon } from "geojson";

import { LayerId, LayerIds } from "fond/layers";
import { MapContext } from "fond/map/MapProvider";

import * as turf from "../turf";

const GHOST_COLOR = "#999999";
const GHOST_OPACITY = 0.4;

/**
 * Mapbox layer config for rendering ghost (faded grey) versions of each layer
 * during polygon select. Keyed by LayerId so we don't need to inspect feature geometry at runtime.
 */
const GHOST_LAYER_CONFIG: Partial<
  Record<LayerId, { type: "circle" | "line" | "fill" | "symbol"; paint: Record<string, unknown>; layout?: Record<string, unknown> }>
> = {
  [LayerIds.inAddress]: {
    type: "symbol",
    layout: {
      "text-field": "\uE8C8",
      "text-size": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 12],
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "text-padding": 0,
      "text-line-height": -0.45,
      "text-anchor": "bottom",
      "text-rotate": 0.01,
      "text-justify": "center",
      "text-offset": [0.05, 0],
      "text-font": ["fontello Regular"],
    },
    paint: {
      "text-opacity": GHOST_OPACITY,
      "text-halo-color": GHOST_COLOR,
      "text-halo-width": 1,
    },
  },
  [LayerIds.inParcel]: {
    type: "fill",
    paint: { "fill-color": GHOST_COLOR, "fill-opacity": GHOST_OPACITY, "fill-outline-color": GHOST_COLOR },
  },
  [LayerIds.inStreet]: {
    type: "line",
    paint: { "line-color": GHOST_COLOR, "line-width": 2, "line-opacity": GHOST_OPACITY },
  },
  [LayerIds.inBaseDataStreet]: {
    type: "line",
    paint: { "line-color": GHOST_COLOR, "line-width": 2, "line-opacity": GHOST_OPACITY },
  },
  [LayerIds.inSpan]: {
    type: "line",
    paint: { "line-color": GHOST_COLOR, "line-width": 2, "line-opacity": GHOST_OPACITY },
  },
  [LayerIds.inPole]: {
    type: "symbol",
    layout: {
      "text-field": "\uE828",
      "text-size": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 22],
      "text-allow-overlap": true,
      "text-ignore-placement": true,
      "text-padding": 0,
      "text-line-height": -0.45,
      "text-anchor": "bottom",
      "text-rotate": 0.01,
      "text-justify": "center",
      "text-font": ["fontello Regular"],
    },
    paint: {
      "text-color": GHOST_COLOR,
      "text-opacity": GHOST_OPACITY,
      "text-halo-color": GHOST_COLOR,
      "text-halo-width": ["interpolate", ["linear"], ["zoom"], 10, 0, 18, 2],
    },
  },
};

interface IProps {
  id: LayerId;
  existingData: FeatureCollection;
  selectedArea: Feature<Polygon>;
}

/**
 * Renders existing project data as a faded grey layer during polygon select,
 * so the user can distinguish old data from newly selected data.
 *
 * Features inside the drawn polygon are excluded to avoid overlapping
 * with the new area select data.
 */
const PolygonSelectGhostLayer: React.FC<IProps> = ({ id, existingData, selectedArea }) => {
  const { map } = useContext(MapContext);
  const sourceId = `${id}-ghost`;
  const mapLayerId = `${id}-ghost`;
  const ghostStyle = GHOST_LAYER_CONFIG[id];

  // Filter existing data to exclude features inside the drawn polygon, since
  // those are being replaced by new area select data. Only recomputes when
  // existingData or selectedArea changes (both are stable Redux references).
  const ghostData = useMemo(() => {
    if (!existingData?.features?.length) return null;
    if (!selectedArea?.geometry) return existingData;

    const filtered = existingData.features.filter((f: Feature) => {
      try {
        if (f.geometry?.type === "Point") {
          return !turf.booleanPointInPolygon(f, selectedArea);
        }
        return !turf.booleanIntersects(f, selectedArea);
      } catch {
        return true;
      }
    });
    return filtered.length > 0 ? turf.featureCollection(filtered) : null;
  }, [existingData, selectedArea]);

  const remove = useCallback(() => {
    if (map?.getLayer(mapLayerId)) map?.removeLayer(mapLayerId);
    if (map?.getSource(sourceId)) map?.removeSource(sourceId);
  }, [map, mapLayerId, sourceId]);

  const add = useCallback(() => {
    if (!map || !ghostData?.features?.length || !ghostStyle) return;

    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: "geojson", data: ghostData });
    }
    if (!map.getLayer(mapLayerId)) {
      map.addLayer({
        id: mapLayerId,
        type: ghostStyle.type,
        source: sourceId,
        paint: ghostStyle.paint,
        ...(ghostStyle.layout ? { layout: ghostStyle.layout } : {}),
      } as mapboxgl.AnyLayer);
    }
  }, [map, ghostData, ghostStyle, sourceId, mapLayerId]);

  useEffect(() => {
    add();

    const handleStyleData = () => add();
    const handleStyleDataLoading = () => {
      map?.once("styledata", handleStyleData);
    };
    map?.on("styledataloading", handleStyleDataLoading);

    return () => {
      map?.off("styledataloading", handleStyleDataLoading);
      map?.off("styledata", handleStyleData);
      remove();
    };
  }, [add, remove, map]);

  useEffect(() => {
    if (!ghostData?.features?.length) {
      remove();
      return;
    }
    const source = map?.getSource(sourceId) as mapboxgl.GeoJSONSource | undefined;
    if (source) {
      source.setData(ghostData);
    } else {
      add();
    }
  }, [ghostData, add, remove, map, sourceId]);

  return null;
};

export default PolygonSelectGhostLayer;
